import {
    Component,
    ChangeDetectionStrategy,
    OnInit,
    OnDestroy,
    ChangeDetectorRef,
} from '@angular/core';
import {
    FormInputComponent,
    DataService,
    StringCustomFieldConfig,
    SharedModule,
    PROMOTION_FRAGMENT,
    ModalService,
    NotificationService,
} from '@vendure/admin-ui/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
    combineLatest, distinctUntilChanged, filter, firstValueFrom,
    map, shareReplay, startWith, Subject, takeUntil,
} from 'rxjs';
import QRCode from 'qrcode';
import { saveAs } from 'file-saver';
import gql from 'graphql-tag';
import { HtmlDialogComponent } from '@common-ui';
import { PROMOTION_HINT_MESSAGE } from '../../common/ui/constants';

const UPDATE_PROMOTION_LINK_MUTATION = gql`
    mutation UpdatePromotionLink($id: ID!) {
        updatePromotionLink(id: $id) {
            ...Promotion
        }
    }
    ${PROMOTION_FRAGMENT}
`;

const GET_STORE_URL = gql`
    query GetStoreUrl {
        getStoreSiteByChannel {
            id
            storeUrl
        }
    }
`;

const GENERATION_PROMO_LINK_CONFIRMATION = `
    <p>
        If you have edits on this promotion that haven't been saved yet.<br>
        Press <strong>Cancel</strong> to go back and hit <span><strong>Update</strong></span> first.<br>
        Or <strong>Continue</strong> to discard the pending changes and create a promo link.
    </p>
`;

@Component({
    template: `
    <div class="promo-link-wrapper">
        <div class="link-row">
            <span *ngIf="promoLinkUrl" class="copy-wrap">
                <button type="button" class="icon-btn" (click)="copyLink()" title="Copy link" aria-label="Copy link">
                    <clr-icon shape="copy"></clr-icon>
                </button>
                <a [href]="promoLinkUrl" target="_blank" class="link mono-clip" [title]="promoLinkUrl">
                    {{ promoLinkUrl }}
                    <clr-icon shape="pop-out"></clr-icon>
                </a>
            </span>
            <span *ngIf="!promoLinkUrl" class="placeholder-text">{{ hintText }}</span>
            <button
                *ngIf="promoLinkUrl"
                class="btn btn-primary w-fit"
                (click)="downloadQr()">
                Download QR
            </button>
            <button
                *ngIf="canGenerate"
                class="btn btn-primary w-fit"
                [disabled]="loading"
                (click)="generate()">
                {{ loading ? 'Generating…' : 'Generate Link' }}
            </button>
        </div>
    </div>
    `,
    styles: [`
        .promo-link-wrapper { width: 100%; }
        .link-row {
            display: flex;
            align-items: center;
            gap: 6px;
        }
        .copy-wrap {
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
            flex: 1;
            min-width: 0;
        }
        .icon-btn {
            border: 0;
            background: transparent;
            padding: 0;
            cursor: pointer;
            line-height: 0;
            color: var(--color-text-secondary);
            flex-shrink: 0;
        }
        .icon-btn clr-icon {
            width: 18px;
            height: 18px;
        }
        .mono-clip {
            display: inline-block;
            max-width: 400px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        a.mono-clip:hover {
            text-decoration: underline;
        }
        .placeholder-text {
            flex: 1;
            font-size: 0.75rem;
            color: var(--color-text-300, #888);
        }
    `],
    standalone: true,
    imports: [SharedModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromoLinkFormInputComponent
    implements FormInputComponent<StringCustomFieldConfig>, OnInit, OnDestroy {
    readonly: boolean;
    formControl: UntypedFormControl;
    config: StringCustomFieldConfig;

    storeUrl = '';
    promoLinkUrl = '';
    loading = false;

    /** True when we can call the generate mutation (existing promotion + coupon code present + no link yet) */
    canGenerate = false;

    private promotionId: string | null = null;
    private hasCouponCode = false;
    private destroy$ = new Subject<void>();

    constructor(
        private dataService: DataService,
        private route: ActivatedRoute,
        private modalService: ModalService,
        private notificationService: NotificationService,
        private changeDetector: ChangeDetectorRef,
    ) {}

    get hintText(): string {
        if (!this.promotionId || this.promotionId === 'create') {
            return PROMOTION_HINT_MESSAGE;
        }
        if (!this.hasCouponCode) {
            return PROMOTION_HINT_MESSAGE;
        }
        return PROMOTION_HINT_MESSAGE;
    }

    ngOnInit(): void {
        const promotionId$ = this.route.paramMap.pipe(
            map(pm => pm.get('id')),
            distinctUntilChanged(),
            shareReplay(1),
        );

        const storeSite$ = this.dataService
            .query<any>(GET_STORE_URL, {}, 'network-only')
            .mapStream((d: any) => d.getStoreSiteByChannel)
            .pipe(filter(Boolean), shareReplay(1));

        // promoLinkCode stored in this formControl (custom field)
        const promoLinkCode$ = this.formControl.valueChanges.pipe(
            startWith(this.formControl.value ?? ''),
            distinctUntilChanged(),
        );

        // couponCode is a sibling on the parent promotion FormGroup
        const couponCodeControl = this.formControl.parent?.parent?.get('couponCode') as UntypedFormControl | null;
        const couponCode$ = couponCodeControl
            ? couponCodeControl.valueChanges.pipe(startWith(couponCodeControl.value ?? ''), distinctUntilChanged())
            : [null];

        combineLatest([storeSite$, promotionId$, promoLinkCode$, couponCode$])
            .pipe(
                filter(([site, id]) => !!site && !!id),
                distinctUntilChanged(([as, ai, ac, acp], [bs, bi, bc, bcp]) =>
                    as.id === bs.id && ai === bi && ac === bc && acp === bcp,
                ),
                takeUntil(this.destroy$),
            )
            .subscribe(([site, id, linkCode, couponCode]) => {
                this.storeUrl      = site.storeUrl;
                this.promotionId   = id;
                this.hasCouponCode = !!couponCode;

                if (id === 'create') {
                    this.promoLinkUrl = '';
                    this.canGenerate  = false;
                } else if (linkCode) {
                    // Already has a generated link code — show the URL
                    this.promoLinkUrl = `${site.storeUrl}/promocode/${linkCode}`;
                    this.canGenerate  = false;
                } else {
                    // No link yet — allow generation only if coupon code exists
                    this.promoLinkUrl = '';
                    this.canGenerate  = !!couponCode;
                }

                this.changeDetector.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async generate() {
        if (!(await this.confirmUnsaved())) return;
        this.loading = true;
        this.changeDetector.markForCheck();

        this.dataService.mutate<any>(UPDATE_PROMOTION_LINK_MUTATION, { id: this.promotionId })
            .subscribe({
                next: (res) => {
                    const linkCode = res?.updatePromotionLink?.customFields?.promotionLink;
                    if (linkCode) {
                        this.formControl.setValue(linkCode);
                        this.promoLinkUrl = `${this.storeUrl}/promocode/${linkCode}`;
                        this.canGenerate  = false;
                        this.notificationService.success('Promo link generated successfully');
                    }
                    this.loading = false;
                    this.changeDetector.markForCheck();
                },
                error: (err) => {
                    this.notificationService.error(
                        `Error generating promo link: ${err?.message ?? 'An error occurred'}`,
                    );
                    this.loading = false;
                    this.changeDetector.markForCheck();
                },
            });
    }

    async copyLink() {
        try {
            await navigator.clipboard.writeText(this.promoLinkUrl);
            this.notificationService.success('Link copied to clipboard');
        } catch {
            this.notificationService.error('Failed to copy link');
        }
    }

    async downloadQr() {
        const dataUrl = await QRCode.toDataURL(this.promoLinkUrl);
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const code = this.promoLinkUrl.split('/').pop() ?? 'promo';
        saveAs(blob, `promo-link-qr-${code}.png`);
    }

    private confirmUnsaved(): Promise<boolean> {
        return firstValueFrom(
            this.modalService.fromComponent(HtmlDialogComponent, {
                locals: {
                    title: 'Please check pending changes.',
                    body: GENERATION_PROMO_LINK_CONFIRMATION,
                    buttons: [
                        { type: 'secondary', label: 'Cancel',   returnValue: false },
                        { type: 'danger',    label: 'Continue', returnValue: true  },
                    ],
                },
            }),
        ).then(r => !!r);
    }
}
